import React from 'react'
import {
  makeStyles,
  createStyles,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@material-ui/core'
import CMSText, { prismicText } from '../Global/CMSText'
import Price from './Price'
import { useTranslation } from 'react-i18next'
import { PrismicShippingCost } from '@/interfaces/common/prismicProduct'

type TProps = {
  shippingCosts: PrismicShippingCost[]
}

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    tableContainer: {
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[1],
    },
    table: {
      marginTop: `${theme.spacing(2)}px !important`,
    },
    tableHead: {
      backgroundColor: theme.extraColors.greyLight,
      '& th': {
        fontWeight: 600,
        borderBottom: `1px solid ${theme.extraColors.greyMedium}`,
        fontSize: '10px',
        padding: theme.spacing(0.75, 1.5),
      },
    },
    tableRow: {
      '&:nth-child(even)': {
        backgroundColor: theme.extraColors.greyLight,
      },
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&:last-child td': {
        borderBottom: 0,
      },
    },
    tableCell: {
      borderBottom: `1px solid ${theme.extraColors.greyMedium}`,
      fontSize: '0.875em !important',
      padding: theme.spacing(0.75, 1.5),
    },
    emptyState: {
      textAlign: 'center',
      padding: theme.spacing(3),
      color: theme.palette.text.secondary,
    },
  })
)

/**
 * ShippingCostsTable
 * @description Table to display shipping costs
 * @param {TProps} shippingCosts - Shipping costs
 * @returns {React.ReactNode}
 */
const ShippingCostsTable = ({ shippingCosts }: TProps) => {
  const classes = useStyles()
  const { t } = useTranslation()

  if (!shippingCosts || shippingCosts.length === 0) {
    return (
      <div className={classes.root}>
        <Typography variant="body2" className={classes.emptyState}>
          Aucun frais de livraison disponible
        </Typography>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <Typography variant="h5">
        {t('texts:shop:shipping_costs:title')}
      </Typography>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableCell}>
                {t('texts:shop:shipping_costs:number_of_articles')}
              </TableCell>
              <TableCell className={classes.tableCell}>
                {t('texts:shop:shipping_costs:round_trip_cost')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shippingCosts.map((cost, index) => (
              <TableRow key={index} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>
                  <CMSText data={cost.number_of_articles} />
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Price
                    amount={parseFloat(prismicText(cost.cost_both_ways)) || 0}
                    showDayOrMonth={false}
                    numberOfDays={30}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default ShippingCostsTable
